import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MessageSquare, ArrowLeft, Loader2, Pin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  MessageBubble, 
  MessageInput, 
  ConversationList, 
  PinnedMessagesPanel,
  TypingIndicator 
} from '@/components/messages';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Real-time features
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [readStatus, setReadStatus] = useState({});
  
  // Reactions
  const [availableEmojis, setAvailableEmojis] = useState(["❤️", "👍", "👎", "😂", "😮", "😢", "🔥", "🙏"]);
  const [activeReactionPicker, setActiveReactionPicker] = useState(null);
  
  // Search
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [messageSearchResults, setMessageSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Pinned messages
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  
  // Voice playback
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioPlayerRef = useRef(null);
  
  // File upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileCaption, setFileCaption] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  // Fetch emojis on mount
  useEffect(() => {
    const fetchEmojis = async () => {
      try {
        const response = await axios.get(`${API}/dm/reactions/emojis`);
        if (response.data.emojis) setAvailableEmojis(response.data.emojis);
      } catch (error) {
        console.error('Failed to fetch emojis');
      }
    };
    fetchEmojis();
  }, []);

  // Reaction handlers
  const handleAddReaction = async (messageId, emoji) => {
    try {
      const response = await axios.post(`${API}/dm/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, reactions: response.data.reactions } : msg
      ));
      setActiveReactionPicker(null);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleRemoveReaction = async (messageId) => {
    try {
      const response = await axios.delete(`${API}/dm/messages/${messageId}/reactions`);
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, reactions: response.data.reactions } : msg
      ));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Pin handlers
  const handlePinMessage = async (messageId) => {
    try {
      const response = await axios.post(`${API}/dm/messages/${messageId}/pin`);
      if (response.data.status === 'ok') {
        setMessages(prev => prev.map(msg => 
          msg.message_id === messageId ? { ...msg, pinned: true, pinned_at: response.data.pinned_at } : msg
        ));
        toast.success(t('message_pinned'));
        if (conversationId) fetchPinnedMessages(conversationId);
      }
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUnpinMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/dm/messages/${messageId}/pin`);
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, pinned: false, pinned_at: null } : msg
      ));
      setPinnedMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success(t('message_unpinned'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const fetchPinnedMessages = useCallback(async (convId) => {
    try {
      const response = await axios.get(`${API}/dm/conversations/${convId}/pinned`);
      setPinnedMessages(response.data.pinned_messages || []);
    } catch (error) {
      console.error('Failed to fetch pinned messages');
    }
  }, []);

  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingDuration(0);
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !conversationId) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      formData.append('duration', recordingDuration.toString());
      
      await axios.post(
        `${API}/dm/conversations/${conversationId}/voice`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      fetchConversation(conversationId);
      setAudioBlob(null);
      setRecordingDuration(0);
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSending(false);
    }
  };

  const togglePlayVoice = (messageId, voiceUrl) => {
    if (playingVoiceId === messageId) {
      audioPlayerRef.current?.pause();
      setPlayingVoiceId(null);
    } else {
      if (audioPlayerRef.current) audioPlayerRef.current.pause();
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${voiceUrl}`);
      audio.onended = () => setPlayingVoiceId(null);
      audio.play();
      audioPlayerRef.current = audio;
      setPlayingVoiceId(messageId);
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('file_too_large'));
      return;
    }
    
    setSelectedFile(file);
    setFileCaption('');
    
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const cancelFileUpload = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileCaption('');
  };

  const sendFileMessage = async () => {
    if (!selectedFile || !conversationId) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('caption', fileCaption);
      
      await axios.post(
        `${API}/dm/conversations/${conversationId}/file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      fetchConversation(conversationId);
      cancelFileUpload();
      toast.success(t('success'));
    } catch (error) {
      toast.error(getErrorMessage(error, t('error')));
    } finally {
      setUploadingFile(false);
    }
  };

  // Search handlers
  const searchMessages = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setMessageSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/dm/search`, { params: { q: query, limit: 30 } });
      setMessageSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setIsSearching(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (messageSearchQuery.length >= 2) {
        searchMessages(messageSearchQuery);
      } else {
        setMessageSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [messageSearchQuery, searchMessages]);

  const goToSearchResult = (result) => {
    navigate(`/messages/${result.conversation_id}`);
    setShowSearchResults(false);
    setMessageSearchQuery('');
  };

  // Conversation handlers
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dm/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConversation = useCallback(async (convId) => {
    try {
      const response = await axios.get(`${API}/dm/conversations/${convId}`);
      setActiveConversation(response.data);
      setMessages(response.data.messages || []);
      fetchConversations();
    } catch (error) {
      toast.error(t('error'));
    }
  }, [fetchConversations, t]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (conversationId) fetchConversation(conversationId);
  }, [conversationId, fetchConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      fetchPinnedMessages(conversationId);
    } else {
      setPinnedMessages([]);
    }
  }, [conversationId, fetchPinnedMessages]);

  // Poll for new messages
  useEffect(() => {
    if (!conversationId) return;
    const interval = setInterval(() => fetchConversation(conversationId), 5000);
    return () => clearInterval(interval);
  }, [conversationId, fetchConversation]);

  // Typing indicator handlers
  const sendTypingStatus = useCallback(async () => {
    if (!conversationId) return;
    try {
      await axios.post(`${API}/dm/conversations/${conversationId}/typing`);
    } catch (error) {
      // Silently ignore typing status errors
    }
  }, [conversationId]);

  const clearTypingStatus = useCallback(async () => {
    if (!conversationId) return;
    try {
      await axios.delete(`${API}/dm/conversations/${conversationId}/typing`);
    } catch (error) {
      // Silently ignore typing status errors
    }
  }, [conversationId]);

  const fetchTypingStatus = useCallback(async () => {
    if (!conversationId) return;
    try {
      const response = await axios.get(`${API}/dm/conversations/${conversationId}/typing`);
      setTypingUsers(response.data.typing_users || []);
    } catch (error) {
      // Silently ignore typing status errors
    }
  }, [conversationId]);

  const fetchReadStatus = useCallback(async () => {
    if (!conversationId) return;
    try {
      const response = await axios.get(`${API}/dm/conversations/${conversationId}/read-status`);
      const statusMap = {};
      (response.data.read_status || []).forEach(msg => {
        statusMap[msg.message_id] = { read: msg.read, read_at: msg.read_at };
      });
      setReadStatus(statusMap);
    } catch (error) {
      // Silently ignore read status errors
    }
  }, [conversationId]);

  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      sendTypingStatus();
    }
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      clearTypingStatus();
    }, 3000);
  }, [isTyping, sendTypingStatus, clearTypingStatus]);

  useEffect(() => {
    if (!conversationId) return;
    fetchTypingStatus();
    const interval = setInterval(fetchTypingStatus, 2000);
    return () => {
      clearInterval(interval);
      clearTypingStatus();
    };
  }, [conversationId, fetchTypingStatus, clearTypingStatus]);

  useEffect(() => {
    if (!conversationId) return;
    fetchReadStatus();
    const interval = setInterval(fetchReadStatus, 3000);
    return () => clearInterval(interval);
  }, [conversationId, fetchReadStatus]);

  useEffect(() => {
    if (!messageInput && isTyping) {
      setIsTyping(false);
      clearTypingStatus();
    }
  }, [messageInput, isTyping, clearTypingStatus]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !conversationId) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/dm/conversations/${conversationId}/messages`, {
        content: messageInput.trim()
      });
      setMessageInput('');
      fetchConversation(conversationId);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    navigate(`/messages/${conv.conversation_id}`);
  };

  return (
    <TooltipProvider>
      <div className="h-screen bg-slate-950 text-white flex flex-col">
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-4 flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">{t('messages')}</h1>
              <p className="text-xs text-slate-400">{t('direct_messages')}</p>
            </div>
          </div>
        </header>

        <div className="flex-1 flex min-h-0">
          {/* Conversations List */}
          <ConversationList
          conversations={conversations}
          loading={loading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          messageSearchQuery={messageSearchQuery}
          setMessageSearchQuery={setMessageSearchQuery}
          isSearching={isSearching}
          showSearchResults={showSearchResults}
          messageSearchResults={messageSearchResults}
          onSelectConversation={selectConversation}
          onGoToSearchResult={goToSearchResult}
          onClearSearch={() => { setMessageSearchQuery(''); setShowSearchResults(false); }}
          activeConversationId={conversationId}
        />

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!conversationId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <h3 className="text-xl font-semibold mb-2">{t('conversations')}</h3>
                <p className="text-slate-400">{t('start_conversation')}</p>
              </div>
            </div>
          ) : activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={activeConversation.other_user?.picture} />
                    <AvatarFallback>{activeConversation.other_user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{activeConversation.other_user?.name}</h3>
                    {activeConversation.other_user?.user_subcategory && (
                      <p className="text-xs text-slate-400 capitalize">
                        {t(activeConversation.other_user.user_subcategory) || activeConversation.other_user.user_subcategory}
                      </p>
                    )}
                  </div>
                </div>
                
                {pinnedMessages.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPinnedPanel(!showPinnedPanel)}
                        className={showPinnedPanel ? 'bg-amber-500/20 text-amber-400' : ''}
                        data-testid="pinned-toggle"
                      >
                        <Pin className="w-4 h-4 mr-1" />
                        {pinnedMessages.length}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('pinned_messages')}</TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Pinned Messages Panel */}
              <PinnedMessagesPanel
                show={showPinnedPanel}
                pinnedMessages={pinnedMessages}
                onClose={() => setShowPinnedPanel(false)}
                onUnpinMessage={handleUnpinMessage}
              />

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <MessageBubble
                      key={msg.message_id || idx}
                      msg={msg}
                      isOwn={msg.sender_id === user?.user_id}
                      readStatus={readStatus}
                      userId={user?.user_id}
                      availableEmojis={availableEmojis}
                      activeReactionPicker={activeReactionPicker}
                      setActiveReactionPicker={setActiveReactionPicker}
                      onAddReaction={handleAddReaction}
                      onRemoveReaction={handleRemoveReaction}
                      onPinMessage={handlePinMessage}
                      onUnpinMessage={handleUnpinMessage}
                      playingVoiceId={playingVoiceId}
                      onTogglePlayVoice={togglePlayVoice}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Typing Indicator */}
              <TypingIndicator typingUsers={typingUsers} />

              {/* Message Input */}
              <MessageInput
                messageInput={messageInput}
                setMessageInput={setMessageInput}
                sending={sending}
                onSendMessage={handleSendMessage}
                onTyping={handleTyping}
                isRecording={isRecording}
                recordingDuration={recordingDuration}
                audioBlob={audioBlob}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onCancelRecording={cancelRecording}
                onSendVoiceMessage={sendVoiceMessage}
                selectedFile={selectedFile}
                filePreview={filePreview}
                fileCaption={fileCaption}
                setFileCaption={setFileCaption}
                uploadingFile={uploadingFile}
                onFileSelect={handleFileSelect}
                onCancelFileUpload={cancelFileUpload}
                onSendFileMessage={sendFileMessage}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};

export default MessagesPage;
